import { SessionDetailView, SessionHistoryTabs, viewCanSeeQuickSelects, viewSupportsFilter, viewSupportsNavigation, viewSupportsSearch, viewSupportsSwitch } from "./views";
import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { RootState } from '../../store';
import { useUserData } from '../../lib/redux/users/user-data-hook';
import { SPECIAL_FILTERS, StaticFilterComponent } from '../../components/StaticFilter';
import { Filter, RefreshCwIcon, Search, PhoneCall, MessagesSquare, PhoneForwarded, AlertCircle, Clock, ClipboardCheck, X, User, PhoneOff, ChevronDown, Bookmark, Users, Edit3 } from 'lucide-react';
import { SessionList } from '../../components/SessionList/SessionList';
import { updateForceRefresh } from '../../lib/redux/sessions/lists/list-slice';
import { selectPersonSearchResults, selectNumberSearchResults, useQuickSearches } from '../../lib/redux/sessions/data/search-hook';
import { SessionHistory } from '../../components/SessionHistory/SessionHistory';
import { BackendFilter } from '../../interfaces/sessions';
import { AWARDCO_TEAM_ID } from '../../cfg/const';
import { useTexts } from '../../lib/redux/sessions/texting/hooks';
import { TranscriptDateOptions, updateHistoryFilter } from '../../lib/redux/filters/slice';
import { ContactV2, PersonV2 } from '../../interfaces/db';
import { formatNumber } from '../../components/SessionHistory/number-helpers';
import { searchContactViaPlatformPersonId } from '../../lib/redux/sessions/data/search-slice';
import { NumberSearchItem } from '../../lib/redux/sessions/data/search-slice';
import { Callback } from '../Callbacks/Callback';
import { SESSION_METRICS_COLUMNS, PROSPECT_INFO_COLUMNS, CallMetricColumns } from '../../interfaces/table';
import { useFilterInfo } from '../../lib/redux/filters/filter-info-hook';
import { FilterContext } from '../../lib/redux/filters/slice';
import { useSessionItems } from "lib/redux/sessions/lists/session-items-hook";
import { openEditingModal } from '../../lib/redux/views/slice';
import { SavedView } from '../../interfaces/views';

type HistoryProps = {
  view: SessionDetailView

  forcedFilter?: BackendFilter
  enforcedStart?: Date
  enforcedEnd?: Date

  selectedSessions?: string[]
  onSessionSelect?: (sessionIds: string[]) => void
}

export function History(props: HistoryProps) {
  const { forcedFilter, view, enforcedStart, enforcedEnd, onSessionSelect, selectedSessions } = props

  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const [activeTab, setActiveTab] = useState<SessionHistoryTabs>(SessionHistoryTabs.CALLS)
  
  // Get user state to check parallel_enabled
  const user = useSelector((state: RootState) => state.user.value)
  const parallel_enabled = user?.parallel_enabled && user?.team_id !== AWARDCO_TEAM_ID
  
  // Use the session list hook with proper memoization to prevent re-renders
  const can_text = useSelector((state: RootState) => state.team.can_text)
  const standard_filter = useSelector((state: RootState) => state.historyFilter.value)
  const filter = React.useMemo(() => forcedFilter ?? standard_filter, [forcedFilter, standard_filter])
  const { getFilterText } = useFilterInfo(filter)

  // Saved Views
  const savedViewsRaw = useSelector((state: RootState) => state.savedViews.value)
  const { isAdmin } = useUserData()
  const [savedViewsDropdownOpen, setSavedViewsDropdownOpen] = useState(false)

  // Filter and sort saved views: user's views first, then team views
  const filteredAndSortedViews = React.useMemo(() => {
    if (!user) return []
    
    const savedViews = savedViewsRaw || []
    const relevantViews = savedViews.filter(view => 
      view.is_team || view.user_id === user.user_id
    )
    
    // Sort: user's personal views first, then team views (both sorted alphabetically)
    const userViews = relevantViews
      .filter(view => view.user_id === user.user_id && !view.is_team)
      .sort((a, b) => a.name.localeCompare(b.name))
    
    const teamViews = relevantViews
      .filter(view => view.is_team)
      .sort((a, b) => a.name.localeCompare(b.name))
    
    return [...userViews, ...teamViews]
  }, [savedViewsRaw, user])

  const handleApplySavedView = (savedView: SavedView) => {
    dispatch(updateHistoryFilter(savedView.filter));
    setSavedViewsDropdownOpen(false);
  };

  const handleEditSavedView = (savedView: SavedView, e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(openEditingModal(savedView));
    setSavedViewsDropdownOpen(false);
  };

  const transcriptDateFilter = useSelector((state: RootState) => state.transcriptDateFilter)
  const enforced_start = transcriptDateFilter.dateOptions !== TranscriptDateOptions.ALL 
    ? new Date(transcriptDateFilter.dateRange.startDate) 
    : enforcedStart
  const enforced_end = transcriptDateFilter.dateOptions !== TranscriptDateOptions.ALL 
    ? new Date(transcriptDateFilter.dateRange.endDate) 
    : enforcedEnd

  const { items, isLoading } = useSessionItems({
    passed_filter: filter,
    enforced_start: enforced_start,
    enforced_end: enforced_end,
    tab: activeTab,
    view,
  })

  useTexts()

  const [searchTerm, setSearchTerm] = useState<string>('')
  const [showSearchResults, setShowSearchResults] = useState(false)
  const searchResultsRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const { search, clearSearchResults, searchForPhones, searchForName, searchForConnects, searchForObjections, searchForReviews, clearAllFilters } = useQuickSearches()
  const personSearchResults = useSelector((state: RootState) => selectPersonSearchResults(state, searchTerm))
  const personSearchIsLoading = personSearchResults?.isLoading || false
  const numberSearchResults = useSelector((state: RootState) => selectNumberSearchResults(state, searchTerm))
  const contactSearchResults = useSelector((state: RootState) => state.namedPersonSearch.contactSearches)
  const numberSearchIsLoading = numberSearchResults?.isLoading || false
  const enforceSessionView = new URLSearchParams(location.search).get('view') === 'session'
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null)

  // Add state to track if the refresh animation should be active
  const [isRefreshAnimating, setIsRefreshAnimating] = useState(false);
  // Update the refresh animation state when loading state changes
  useEffect(() => {
    // If loading is starting, ensure the animation is running
    if (isLoading) {
      setIsRefreshAnimating(true);
    } 
    // If loading has finished, stop the animation after a short delay
    else if (!isLoading && isRefreshAnimating) {
      const animationTimeout = setTimeout(() => {
        setIsRefreshAnimating(false);
      }, 500); // Keep spinning for a short time after loading completes for a smoother UX
      
      return () => {
        clearTimeout(animationTimeout);
      };
    }
  }, [isLoading, isRefreshAnimating]);

  // Handle clicks outside search results dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchResultsRef.current && !searchResultsRef.current.contains(event.target as Node) &&
          searchInputRef.current && !searchInputRef.current.contains(event.target as Node)) {
        setShowSearchResults(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSearch = useCallback((term: string) => {
    setSearchTerm(term)
    setShowSearchResults(true)
    
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current)
    }

    if (!term.trim()) {
      clearSearchResults(term)
      return
    }

    debounceTimeout.current = setTimeout(() => {
      search(term)
    }, 500)
  }, [search, clearSearchResults])

  const handleSelectPerson = async (person: PersonV2) => {
    setShowSearchResults(false)
    const contactResult = contactSearchResults[person.platform_person_id]
    let contacts: ContactV2[] | null = contactResult?.items ?? null
    if (!contacts) {
      contacts = await searchContactViaPlatformPersonId(person.platform_person_id, dispatch)
    }
    if (contacts && contacts.length > 0) {
      const phone_numbers = contacts.map(c => c.address)
      searchForPhones(phone_numbers)
    }
    else if (person.name) {
      searchForName(person.name)
    }
  }

  const handleSelectNumber = (number: NumberSearchItem) => {
    setShowSearchResults(false)
    searchForPhones([number.prospect_phone_value])
  }

  const getViewingItemSessionId = () => {
    const pageSessionId = new URLSearchParams(location.search).get('id')
    return pageSessionId ? pageSessionId : undefined
  }

  const updateRefreshCount = () => dispatch(updateForceRefresh())

  const [originalUrlSessionId, setOriginalUrlSessionId] = useState<string | undefined>(() => getViewingItemSessionId())
  const onLoadIsViewingSet = originalUrlSessionId !== undefined
  const [viewingItemSessionId, setViewingItemSessionId] = useState<string | undefined>(originalUrlSessionId)
  const [isFilterOpen, setIsFilterOpen] = useState(viewingItemSessionId === undefined)
  
  const pendingReviewsFromState = useSelector((state: RootState) => state.pendingReviews.value)
  const pendingReviewsCount: number = pendingReviewsFromState ?? 0 

  // Add new state for communication type tabs
  const onSelectSession = (sessionId: string | undefined) => {
    const prepend_path = enforceSessionView ? '/transcripts?view=session&' : '/transcripts?'
    if (!viewSupportsNavigation(view)) {
      setViewingItemSessionId(sessionId);
      // Update selected sessions if onSessionSelect is provided
      if (onSessionSelect && sessionId !== undefined) {
        const newSelectedSessions = selectedSessions ? [...selectedSessions] : [];
        const sessionIndex = newSelectedSessions.indexOf(sessionId);
        
        if (sessionIndex === -1) {
          newSelectedSessions.push(sessionId);
        } else {
          newSelectedSessions.splice(sessionIndex, 1);
        }
        onSessionSelect(newSelectedSessions);
      }
    } else if (sessionId) {
      const path = `${prepend_path}id=${sessionId}`
      navigate(path);
    } else {
      const path = `${prepend_path}`
      navigate(path);
    }
  }

  // Synchronize with URL params
  useEffect(() => {
    if (!viewSupportsNavigation(view)) return
    const pageSessionId = new URLSearchParams(location.search).get('id')
    if (viewingItemSessionId !== pageSessionId) setViewingItemSessionId(pageSessionId ? pageSessionId : undefined)
  }, [location, viewingItemSessionId, view]);

  // Clear originalUrlSessionId when user navigates to a different session
  useEffect(() => {
    if (originalUrlSessionId && viewingItemSessionId && viewingItemSessionId !== originalUrlSessionId) {
      setOriginalUrlSessionId(undefined)
    }
  }, [viewingItemSessionId, originalUrlSessionId]);
  const sessionIdToDetails =  useSelector((state: RootState) => state.sessionDetails.sessionIdToDetails)
  const sessionItem = items.find(item => item.session_id === viewingItemSessionId)
  const sessionDetails = viewingItemSessionId ? sessionIdToDetails[viewingItemSessionId] : undefined
  const phoneNumber = sessionDetails?.data?.session.customer_address ? sessionDetails.data.session.customer_address : sessionItem?.customer_address
  const phone_numbers = phoneNumber ? [phoneNumber] : []

  const renderSessionDetails = () => {
    if (!viewingItemSessionId) return null
    const isOriginalUrlSession = onLoadIsViewingSet && viewingItemSessionId === originalUrlSessionId
    return <SessionHistory 
      view={view}
      onClose={() => onSelectSession(undefined)}
      sessionId={viewingItemSessionId}
      phone_numbers={phone_numbers}
      enforceSessionView={enforceSessionView}
      onLoadIsViewingSet={isOriginalUrlSession}
    />
  }

  const isTight = ((filter && filter.length > 0) && window.innerWidth < 750) || window.innerWidth < 400 || props.view === SessionDetailView.COACHING

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  return (
    <div className="h-full flex flex-col">
      {/* Click outside to close saved views dropdown */}
      {savedViewsDropdownOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setSavedViewsDropdownOpen(false)}
        />
      )}

      {/* Navigation tabs on their own row */}
      {viewSupportsSwitch(view) && <div className="border-b border-gray-200 px-3 py-2 bg-white flex-shrink-0">

        <div className="flex items-center justify-between gap-1">
          {/* Tab selector */}
          <div className="flex-shrink-0">
            <div className={`items-center border rounded-md bg-gray-50 overflow-hidden shadow-sm ${isTight ? 'hidden' : 'flex'}`}>
              <button
                onClick={() => setActiveTab(SessionHistoryTabs.CALLS)}
                className={`flex items-center py-1.5 px-5 text-sm font-medium transition-colors whitespace-nowrap ${
                  activeTab === 'calls' 
                    ? 'bg-white text-indigo-600 shadow-sm' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <PhoneCall className="w-4 h-4 mr-1.5" />
                <span>Calls</span>
              </button>
              <button
                onClick={() => setActiveTab(SessionHistoryTabs.CALLBACKS)}
                className={`flex items-center py-1.5 px-5 text-sm font-medium transition-colors whitespace-nowrap ${
                  activeTab === 'callbacks' 
                    ? 'bg-white text-indigo-600 shadow-sm' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <PhoneForwarded className="w-4 h-4 mr-1.5" />
                <span>Callbacks</span>
              </button>
              {parallel_enabled && (
                <button
                  onClick={() => setActiveTab(SessionHistoryTabs.MISSED)}
                  className={`flex items-center py-1.5 px-5 text-sm font-medium transition-colors whitespace-nowrap ${
                    activeTab === 'missed' 
                      ? 'bg-white text-indigo-600 shadow-sm' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <PhoneOff className="w-4 h-4 mr-1.5" />
                  <span>Missed</span>
                </button>
              )}
              {can_text && <button
                onClick={() => setActiveTab(SessionHistoryTabs.TEXTS)}
                className={`flex items-center py-1.5 px-5 text-sm font-medium transition-colors whitespace-nowrap ${
                  activeTab === 'texts' 
                    ? 'bg-white text-indigo-600 shadow-sm' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <MessagesSquare className="w-4 h-4 mr-1.5" />
                <span>Texts</span>
              </button>}
            </div>

            {/* Dropdown for when space is actually tight */}
            <div className={`${isTight ? 'block' : 'hidden'} relative`}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center justify-between w-[200px] h-9 px-3 rounded-md border border-gray-300 bg-white text-sm"
              >
                <div className="flex items-center">
                  {activeTab === 'calls' && <PhoneCall className="w-4 h-4 mr-1.5 text-gray-600" />}
                  {activeTab === 'callbacks' && <PhoneForwarded className="w-4 h-4 mr-1.5 text-gray-600" />}
                  {activeTab === 'missed' && <PhoneOff className="w-4 h-4 mr-1.5 text-gray-600" />}
                  {activeTab === 'texts' && <MessagesSquare className="w-4 h-4 mr-1.5 text-gray-600" />}
                  <span>{activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}</span>
                </div>
                <svg className={`w-4 h-4 text-gray-400 transform transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
              
              {isDropdownOpen && (
                <div className="absolute mt-1 w-[200px] bg-white rounded-md shadow-lg border border-gray-200" style={{ zIndex: 9999 }}>
                  <div className="py-1">
                    <button
                      onClick={() => {
                        setActiveTab(SessionHistoryTabs.CALLS);
                        setIsDropdownOpen(false);
                      }}
                      className={`w-full flex items-center px-4 py-2 text-sm ${activeTab === 'calls' ? 'bg-indigo-50 text-indigo-600' : 'text-gray-700 hover:bg-gray-50'}`}
                    >
                      <PhoneCall className="w-4 h-4 mr-1.5" />
                      Calls
                    </button>
                    <button
                      onClick={() => {
                        setActiveTab(SessionHistoryTabs.CALLBACKS);
                        setIsDropdownOpen(false);
                      }}
                      className={`w-full flex items-center px-4 py-2 text-sm ${activeTab === 'callbacks' ? 'bg-indigo-50 text-indigo-600' : 'text-gray-700 hover:bg-gray-50'}`}
                    >
                      <PhoneForwarded className="w-4 h-4 mr-1.5" />
                      Callbacks
                    </button>
                    {parallel_enabled && (
                      <button
                        onClick={() => {
                          setActiveTab(SessionHistoryTabs.MISSED);
                          setIsDropdownOpen(false);
                        }}
                        className={`w-full flex items-center px-4 py-2 text-sm ${activeTab === 'missed' ? 'bg-indigo-50 text-indigo-600' : 'text-gray-700 hover:bg-gray-50'}`}
                      >
                        <PhoneOff className="w-4 h-4 mr-1.5" />
                        Missed
                      </button>
                    )}
                    {can_text && (
                      <button
                        onClick={() => {
                          setActiveTab(SessionHistoryTabs.TEXTS);
                          setIsDropdownOpen(false);
                        }}
                        className={`w-full flex items-center px-4 py-2 text-sm ${activeTab === 'texts' ? 'bg-indigo-50 text-indigo-600' : 'text-gray-700 hover:bg-gray-50'}`}
                      >
                        <MessagesSquare className="w-4 h-4 mr-1.5" />
                        Texts
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center flex-1 min-w-0 mx-4">
            {/* Saved Views Dropdown */}
            {filteredAndSortedViews.length > 0 && view !== SessionDetailView.COACHING && (
              <div className="relative mr-3">
                <button
                  onClick={() => setSavedViewsDropdownOpen(!savedViewsDropdownOpen)}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-md transition-colors"
                >
                  <Bookmark className="h-4 w-4" />
                  <span>Saved Views</span>
                  <ChevronDown className={`h-4 w-4 transition-transform ${savedViewsDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {savedViewsDropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                    <div className="py-1">
                      {(() => {
                        const userViews = filteredAndSortedViews.filter(view => view.user_id === user?.user_id && !view.is_team)
                        const teamViews = filteredAndSortedViews.filter(view => view.is_team)
                        
                        return (
                          <>
                            {/* Personal Views Section */}
                            {userViews.length > 0 && (
                              <>
                                <div className="px-3 py-1 text-xs font-medium text-gray-500 uppercase tracking-wide">
                                  My Views
                                </div>
                                {userViews.map((savedView) => (
                                  <div
                                    key={savedView.view_id}
                                    className="flex items-center justify-between px-3 py-2 hover:bg-gray-50 cursor-pointer group"
                                    onClick={() => handleApplySavedView(savedView)}
                                  >
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                      <User className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                      <span className="text-sm text-gray-700 truncate">
                                        {savedView.name}
                                      </span>
                                    </div>
                                    <button
                                      onClick={(e) => handleEditSavedView(savedView, e)}
                                      className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-100 rounded transition-all"
                                      title="Edit view"
                                    >
                                      <Edit3 className="h-3 w-3 text-gray-500" />
                                    </button>
                                  </div>
                                ))}
                              </>
                            )}
                            
                            {/* Team Views Section */}
                            {teamViews.length > 0 && (
                              <>
                                {userViews.length > 0 && <div className="border-t border-gray-100 my-1" />}
                                <div className="px-3 py-1 text-xs font-medium text-gray-500 uppercase tracking-wide">
                                  Team Views
                                </div>
                                {teamViews.map((savedView) => (
                                  <div
                                    key={savedView.view_id}
                                    className="flex items-center justify-between px-3 py-2 hover:bg-gray-50 cursor-pointer group"
                                    onClick={() => handleApplySavedView(savedView)}
                                  >
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                      <Users className="h-4 w-4 text-blue-500 flex-shrink-0" />
                                      <span className="text-sm text-gray-700 truncate">
                                        {savedView.name}
                                      </span>
                                    </div>
                                    {/* Only show edit button for team views if user is admin */}
                                    {isAdmin && (
                                      <button
                                        onClick={(e) => handleEditSavedView(savedView, e)}
                                        className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-100 rounded transition-all"
                                        title="Edit view"
                                      >
                                        <Edit3 className="h-3 w-3 text-gray-500" />
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </>
                            )}
                          </>
                        )
                      })()}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Filter text display */}
            {(filter && filter.length > 0) && view !== SessionDetailView.COACHING && (
              <div className="flex-1 min-w-0">
                <div 
                  className="inline-flex max-w-full bg-indigo-50 text-indigo-700 px-3 py-1.5 rounded-md text-sm font-medium items-center group relative"
                >
                  <span className="text-indigo-700 font-semibold">Filter:</span>
                  <span className="truncate ml-2">
                    {getFilterText()}
                  </span>
                  <button
                    onClick={() => clearAllFilters()}
                    className="ml-2 p-1 hover:bg-indigo-100 rounded-full flex-shrink-0"
                    title="Clear filters"
                  >
                    <X className="w-3.5 h-3.5 text-indigo-600" />
                  </button>
                  
                  {/* Hover tooltip with full filter text */}
                  {filter && (
                    <div className="absolute left-0 top-full mt-1 z-50 hidden group-hover:block">
                      <div className="bg-gray-900 text-white px-4 py-2 rounded-md text-sm whitespace-normal max-w-md shadow-lg">
                        {getFilterText()}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          {viewSupportsFilter(view) && 
          <div className="flex items-center space-x-1.5 flex-shrink-0">
            <button
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className={`flex items-center justify-center h-9 w-9 rounded-md border transition-colors ${
                isFilterOpen 
                  ? 'bg-indigo-50 text-indigo-600 border-indigo-200' 
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-600 border-gray-300'
              }`}
              aria-label="Filter"
              title="Filter"
            >
              <Filter className="w-5 h-5" />
            </button>
          </div>}

            <button
              onClick={() => updateRefreshCount()}
              disabled={isRefreshAnimating}
              className={`flex items-center justify-center h-9 w-9 rounded-md border transition-colors ${
                isRefreshAnimating
                  ? 'bg-blue-50 text-blue-600 border-blue-200 cursor-wait'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-600 border-gray-300'
              }`}
              aria-label="Refresh"
              title={isRefreshAnimating ? "Refreshing..." : "Refresh"}
            >
              <RefreshCwIcon 
                className={`w-5 h-5 ${isRefreshAnimating ? 'animate-spin' : ''}`} 
              />
            </button>
          </div>
        </div>}

        {/* Clear filter row for coaching view */}
        {(filter && filter.length > 0) && view === SessionDetailView.COACHING && (
          <div className="border-b border-gray-200 px-3 py-2 bg-white flex-shrink-0">
            <button
              onClick={() => clearAllFilters()}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-red-50 text-red-600 hover:bg-red-100 rounded text-xs font-medium transition-colors"
            >
              <span>Clear</span>
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      
      {/* Search and quick filter row */}
      {viewSupportsSearch(view) && <div className="border-b border-gray-200 px-3 py-2 bg-gray-50 flex-shrink-0">
        <div className="flex items-center gap-2">
          {/* Search box with dropdown */}
          <div className="flex-grow relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search by phone or name..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              onFocus={() => setShowSearchResults(true)}
              className="w-full h-9 pl-9 pr-3 rounded-md border border-gray-300 text-sm bg-white focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 transition-colors"
            />
            
            {/* Search Results Dropdown */}
            {showSearchResults && (
              <div 
                ref={searchResultsRef}
                className="absolute z-50 top-full left-0 right-0 mt-1 bg-white rounded-md shadow-lg border border-gray-200 max-h-64 overflow-y-auto"
              >
                {(() => {
                  if (personSearchIsLoading || numberSearchIsLoading) {
                    return (
                      <div className="p-4 text-gray-500">
                        <div className="flex items-center justify-center space-x-2">
                          <div className="animate-spin rounded-full h-5 w-5 border-2 border-indigo-500 border-t-transparent"></div>
                          <span className="text-sm">Searching...</span>
                        </div>
                      </div>
                    );
                  }

                  // Check if search term is empty
                  if (searchTerm.trim() === '') {
                    return (
                      <div className="p-4 text-gray-500">
                        <div className="flex flex-col space-y-3">
                          <div className="flex items-center text-sm font-medium text-gray-700">
                            <Search className="w-4 h-4 mr-2" />
                            <span>Search by:</span>
                          </div>
                          <div className="space-y-2 ml-6">
                            <div className="flex items-center text-sm text-gray-600">
                              <PhoneCall className="w-4 h-4 mr-2 text-gray-400" />
                              <span>Complete phone number (e.g. "1234567890")</span>
                            </div>
                            <div className="flex items-center text-sm text-gray-600">
                              <User className="w-4 h-4 mr-2 text-gray-400" />
                              <span>Contact name (e.g. "John Smith")</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  // Check if it's an incomplete phone number
                  if (/^\d+$/.test(searchTerm) && searchTerm.length < 10) {
                    return (
                      <div className="p-4">
                        <div className="flex items-center justify-center space-x-2 text-amber-600">
                          <PhoneCall className="w-5 h-5" />
                          <span className="text-sm font-medium">
                            Keep typing... ({searchTerm.length}/10 digits)
                          </span>
                        </div>
                        <p className="text-center text-sm text-gray-500 mt-1">
                          Please enter a complete 10-digit phone number
                        </p>
                      </div>
                    );
                  }

                  // Check if we have results - fix the structure access
                  const hasPersonResults = personSearchResults?.items && personSearchResults.items.length > 0;
                  const hasNumberResults = numberSearchResults?.items && numberSearchResults.items.length > 0;
                  
                  if (hasPersonResults || hasNumberResults) {
                    return (
                      <div className="py-1">
                        {/* Person Results Section */}
                        {hasPersonResults && (
                          <>
                            <div className="px-3 py-2 text-xs font-medium text-gray-500 bg-gray-50">People</div>
                            {personSearchResults?.items.map((person: PersonV2, index: number) => (
                              <button
                                key={`${person.name}-${person.platform_person_id || index}`}
                                onClick={() => handleSelectPerson(person)}
                                className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center space-x-3"
                              >
                                <div className="flex-shrink-0">
                                  <div className="w-8 h-8 rounded-full bg-indigo-50 flex items-center justify-center">
                                    <User className="w-4 h-4 text-indigo-600" />
                                  </div>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium text-gray-900 truncate">
                                    {person.name}
                                  </div>
                                  {person.title && (
                                    <div className="text-sm text-gray-500 truncate">
                                      {person.title}
                                    </div>
                                  )}
                                </div>
                              </button>
                            ))}
                          </>
                        )}

                        {/* Phone Number Results Section */}
                        {hasNumberResults && (
                          <>
                            <div className="px-3 py-2 text-xs font-medium text-gray-500 bg-gray-50">Phone Numbers</div>
                            {numberSearchResults.items.map((number, index) => (
                              <button
                                key={`${number.prospect_phone_value}-${index}`}
                                onClick={() => handleSelectNumber(number)}
                                className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center space-x-3"
                              >
                                <div className="flex-shrink-0">
                                  <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center">
                                    <PhoneCall className="w-4 h-4 text-blue-600" />
                                  </div>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium text-gray-900 truncate">
                                    {number.person_name || 'Unknown'}
                                  </div>
                                  <div className="text-sm text-gray-500 truncate">
                                    {formatNumber(number.prospect_phone_value)}
                                  </div>
                                </div>
                              </button>
                            ))}
                          </>
                        )}
                      </div>
                    );
                  }

                  // No results found
                  return (
                    <div className="p-4 text-center">
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 mb-3">
                        <Search className="w-6 h-6 text-gray-400" />
                      </div>
                      <p className="text-sm font-medium text-gray-900">No results found</p>
                      <p className="text-sm text-gray-500 mt-1">
                        {/^\d+$/.test(searchTerm) && searchTerm.length >= 10
                          ? "No matches found for this phone number" 
                          : "Try adjusting your search terms"}
                      </p>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
          
          {/* Quick filter buttons */}
          { viewCanSeeQuickSelects(view) && <div className="flex items-center space-x-1.5">
            <button 
              onClick={() => searchForConnects()}
              className="flex items-center justify-center h-9 px-3.5 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-md text-sm font-medium border border-blue-200 transition-colors shadow-sm"
              title="Connects"
            >
              <Clock className="w-4 h-4 mr-1.5" />
              <span>Connects</span>
            </button>
            
            <button 
              onClick={() => searchForObjections()}
              className="flex items-center justify-center h-9 px-3.5 bg-orange-50 hover:bg-orange-100 text-orange-600 rounded-md text-sm font-medium border border-orange-200 transition-colors shadow-sm"
              title="Objections"
            >
              <AlertCircle className="w-4 h-4 mr-1.5" />
              <span>Objections</span>
            </button>
            
            <button 
              onClick={() => {
                if (pendingReviewsCount > 0) {
                  searchForReviews()
                }
              }}
              className={`flex items-center justify-center h-9 px-3.5 rounded-md text-sm font-medium border transition-colors shadow-sm ${
                pendingReviewsCount > 0 
                  ? 'bg-pink-50 hover:bg-pink-100 text-pink-600 border-pink-200 cursor-pointer' 
                  : 'bg-gray-100 text-gray-400 border-gray-200 cursor-default'
              }`}
              title={pendingReviewsCount > 0 ? "Pending reviews" : "No pending reviews"}
              disabled={pendingReviewsCount <= 0}
            >
              <ClipboardCheck className={`w-4 h-4 mr-1.5 ${pendingReviewsCount > 0 ? 'text-pink-600' : 'text-gray-400'}`} />
              <span>{pendingReviewsCount > 0 ? pendingReviewsCount : '-'}</span>
            </button>
          </div>}

        </div>
      </div>}
      
      {/* Main content area */}
      <div className="flex flex-1 min-w-0 overflow-x-auto">
        <div className="flex min-w-fit w-full h-full">
          {/* Filter panel - now on the left side with width based on content */}
          {viewSupportsFilter(view) && isFilterOpen && (
            <div className="w-fit border-r border-gray-200 bg-white overflow-auto">
                <StaticFilterComponent
                  applicable_filters={[
                    // All available session columns
                    SESSION_METRICS_COLUMNS.STAGE,
                    SESSION_METRICS_COLUMNS.IS_ANSWERED,
                    SESSION_METRICS_COLUMNS.CALL_DURATION,
                    SESSION_METRICS_COLUMNS.PLATFORM,
                    SESSION_METRICS_COLUMNS.BEST_COUNTERPART,
                    SESSION_METRICS_COLUMNS.USER_DISPOSITION,
                    SESSION_METRICS_COLUMNS.USER_SENTIMENT,
                    SESSION_METRICS_COLUMNS.USER_PURPOSE,
                    SESSION_METRICS_COLUMNS.IS_CONNECTED,
                    SESSION_METRICS_COLUMNS.TRELLUS_DISPOSITION,
                    // All available call metric columns
                    CallMetricColumns.IS_INBOUND,
                    SESSION_METRICS_COLUMNS.LIVE_DURATION,
                    // All available prospect columns
                    PROSPECT_INFO_COLUMNS.CADENCE,
                    PROSPECT_INFO_COLUMNS.COMPANY_INDUSTRY,
                    PROSPECT_INFO_COLUMNS.PROSPECT_TITLE,
                    PROSPECT_INFO_COLUMNS.PROSPECT_LIST,
                    PROSPECT_INFO_COLUMNS.MARKET,
                    PROSPECT_INFO_COLUMNS.LEAD_TYPE,
                  ]}
                  isOpen={true}
                  onClose={() => setIsFilterOpen(false)}
                  special_filters={[SPECIAL_FILTERS.SELECTED_REPS, SPECIAL_FILTERS.CUSTOM_METRICS, SPECIAL_FILTERS.TRANSCRIPT_DATE]}
                  filterContext={FilterContext.HISTORY}
                />
            </div>
          )}
          
          {/* Session list */}
          <div className={`overflow-auto ${
            viewingItemSessionId && view !== SessionDetailView.COACHING 
              ? activeTab === SessionHistoryTabs.CALLBACKS
                ? "w-[400px] min-w-[400px] border-r border-gray-200"
                : "w-72 min-w-[200px] max-w-xs flex-shrink-1 border-r border-gray-200"
              : "flex-1 w-full"
          }`}>
            {activeTab === SessionHistoryTabs.CALLBACKS ? (
              <Callback 
                view={view}
                tab={activeTab}
                passed_filter={filter}
                enforced_start={enforced_start}
                enforced_end={enforced_end}
                onRowClick={(sessionId) => onSelectSession(sessionId)}
              />
            ) : (
              <SessionList
                view={view}
                onRowClick={(item: string) => onSelectSession(item)}
                shownSessionId={viewingItemSessionId}
                tab={activeTab}
                selectedSessions={selectedSessions}
                onSessionSelect={onSessionSelect}
                passed_filter={filter}
                enforced_start={enforced_start}
                enforced_end={enforced_end}
              />
            )}
          </div>

          {/* Session details */}
          {viewingItemSessionId && (
            <div className={`${
              view === SessionDetailView.COACHING 
                ? "absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
                : "flex-1 h-full border-l border-gray-200 min-w-[500px]"
            }`}
              onClick={view === SessionDetailView.COACHING ? (e) => { if (e.target === e.currentTarget) onSelectSession(undefined) } : undefined}
            >
              {view === SessionDetailView.COACHING ? (
                <div 
                  className="bg-white rounded-lg h-4/5 overflow-hidden flex flex-col"
                  style={{ width: '90%' }}
                >
                  {renderSessionDetails()}
                </div>
              ) : (
                <div className="h-full w-full overflow-auto">
                  {renderSessionDetails()}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
  